import { useEffect, useRef, useState } from 'react';
import { CaretUp, CaretDown } from '@phosphor-icons/react';
import API from '@/lib/api';

// Hoofdgroepen + sub-categorieën (gedeeld tussen InkomendeDocument & Leveranciers)
export const CATEGORIE_GROEPEN = [
  { groep: 'Gebouwen / lokalen / kantoren / terreinen', items: ['Elektriciteit (productie + kantoor)', 'Gas / verwarming', 'Waterverbruik', 'Brandverzekering', 'Onderhoud machines (gebouwgebonden installaties)', 'Herstellingen gebouw', 'Schoonmaak (extern bedrijf)', "Beveiliging (alarm, camera's)", 'Onroerende voorheffing', 'Syndicuskosten', 'Afvalverwerking / containerkosten', 'Internet / bekabeling in gebouw'] },
  { groep: 'Algemene kosten', items: ['Telefoon & internet abonnementen', 'Softwarelicenties (ERP, Billit, CAD, etc.)', 'Kantoorbenodigdheden', 'Drukwerk (labels, stickers, catalogi)', 'Bankkosten', 'Boekhouder / accountant', 'Advieskosten (juridisch/fiscaal)', 'Marketing & reclame', 'Website / hosting', 'Opleidingen', 'Lidmaatschappen / federaties'] },
  { groep: 'Investeringen', items: ['Machines (rollforming lijnen, decoilers)', 'Gereedschappen (matrijzen, molds)', 'IT hardware (computers, servers)', 'Magazijninrichting (rekken, stellingen)', 'Heftrucks / intern transport', 'Bouw of renovatie van productiehal', 'Zonnepanelen / energie-installaties'] },
  { groep: 'Personeel / uitzendkrachten', items: ['Lonen arbeiders & bedienden', 'Interimkrachten', 'Vakantiegeld', 'Bonussen', 'Werkkledij / PBM (veiligheidsschoenen, helmen)', 'Opleidingen personeel', 'Maaltijdcheques', 'Transportvergoeding'] },
  { groep: 'Administratieve formaliteiten', items: ['Notariskosten', 'Publicaties (Belgisch Staatsblad)', 'Vergunningen (VLAREM, milieu)', 'Certificeringen (CE, ISO)', 'Douanedocumenten', 'Juridische dossiers', 'Registraties (BTW, EORI)'] },
  { groep: 'Rollend materieel (andere)', items: ['Heftrucks', 'Magazijnvoertuigen', 'Palletwagens (elektrisch)', 'Onderhoud & herstellingen', 'Batterijen / laadstations'] },
  { groep: 'Onderaannemingen', items: ['Transporteurs (nationaal/internationaal)', 'Loonwerk (externe productie)', 'Montageploegen', 'Verpakkingsdiensten', 'Technisch onderhoud extern', 'IT outsourcing'] },
  { groep: '(Handels)goederen', items: ['Grondstoffen (staal DX51D, coils, slitband)', 'Halffabricaten', 'Inkoop profielen', 'Bevestigingsmaterialen (pluggen, schroeven)', 'Verpakkingsmateriaal (karton, folie)', 'Labels / private labeling'] },
  { groep: 'Huur', items: ['Huur bedrijfspand', 'Huur magazijn', 'Huur machines', 'Huur voertuigen', 'Leasing (indien als huur geboekt)'] },
  { groep: 'Rollend materieel (personenwagen)', items: ['Aankoop / leasing wagen', 'Brandstof wagen', 'Verzekering wagen', 'Onderhoud wagen', 'Banden', 'Taksen'] },
  { groep: 'Rollend materieel (motorfiets)', items: ['Aankoop motorfiets', 'Brandstof motorfiets', 'Onderhoud motorfiets', 'Verzekering motorfiets'] },
  { groep: 'Rollend materieel (lichte vracht)', items: ['Bestelwagens / camionettes', 'Brandstof bestelwagen', 'Onderhoud bestelwagen', 'Leasing bestelwagen', 'Verzekering bestelwagen', 'Tolkosten'] },
  { groep: 'Erelonen / commissies', items: ['Makelaars', 'Verkoperscommissie', 'Agenten', 'Consultancy fees', 'Technische adviseurs'] },
  { groep: 'Verzekeringen', items: ['BA verzekering', 'Arbeidsongevallen', 'Machinebreuk', 'Transportverzekering', 'Kredietverzekering', 'Brandverzekering (algemeen)'] },
  { groep: 'Sociale bijdragen', items: ['RSZ bijdragen', 'Zelfstandigen bijdragen', 'Sectorbijdragen', 'Pensioenfonds'] },
];

/**
 * Zoekbaar categorie-veld (typeahead).
 * Toont hoofdgroepen + sub-items + eigen categorieën van de gebruiker.
 *
 * Props:
 *  - value: huidig geselecteerde categorie (string)
 *  - onChange(nieuweWaarde): callback
 *  - placeholder: optionele placeholder
 *  - testId: data-testid prefix
 */
export default function CategorieZoekSelect({ value, onChange, placeholder = 'Zoek of kies een categorie...', testId = 'cat-select' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customCats, setCustomCats] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    API.get('/categorie-custom').then(r => setCustomCats(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const pick = (val) => { onChange(val); setOpen(false); setSearch(''); };

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <input
          className="field-input w-full pr-8 cursor-pointer"
          value={open ? search : (value || '')}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => { setSearch(''); setOpen(true); }}
          placeholder={placeholder}
          data-testid={testId}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          {open ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
        </div>
      </div>
      {open && (() => {
        const q = search.toLowerCase().trim();
        const allGroups = [...CATEGORIE_GROEPEN];
        if (customCats.length > 0) {
          const groepMap = {};
          customCats.forEach((c) => {
            const g = c.groep || 'Eigen categorieën';
            if (!groepMap[g]) groepMap[g] = [];
            groepMap[g].push(c.naam);
          });
          Object.entries(groepMap).forEach(([g, items]) => {
            const ex = allGroups.find((x) => x.groep === g);
            if (ex) ex.items = [...ex.items, ...items];
            else allGroups.push({ groep: g, items, _custom: true });
          });
        }
        const filtered = allGroups
          .map((g) => {
            if (!q) return g;
            const mg = g.groep.toLowerCase().includes(q);
            const mi = g.items.filter((it) => it.toLowerCase().includes(q));
            if (mg || mi.length > 0) return { ...g, items: mg ? g.items : mi };
            return null;
          })
          .filter(Boolean);
        const total = filtered.reduce((s, g) => s + g.items.length + 1, 0);
        return (
          <div className="absolute z-30 mt-1 w-full bg-white border border-gray-300 shadow-lg max-h-80 overflow-auto" data-testid={`${testId}-dropdown`}>
            {value && (
              <button
                onClick={() => pick('')}
                className="w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50 border-b border-gray-100 font-semibold"
              >
                × Wissen
              </button>
            )}
            {total === 0 ? (
              <div className="p-4 text-center text-xs text-gray-400">
                Geen resultaten voor <strong>&quot;{q}&quot;</strong>
              </div>
            ) : (
              filtered.map((g, gi) => (
                <div key={g.groep + gi}>
                  <div className="px-3 py-1.5 bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1 border-t border-b border-gray-100">
                    {g._custom && <span className="text-[#D97706]">★</span>} {g.groep}
                  </div>
                  <button
                    onClick={() => pick(g.groep)}
                    className="w-full px-3 py-1.5 text-left text-xs hover:bg-blue-50 text-gray-600 font-medium italic"
                  >
                    {g.groep} <span className="text-gray-400">(hoofdcategorie)</span>
                  </button>
                  {g.items.map((it) => (
                    <button
                      key={it}
                      onClick={() => pick(it)}
                      className="w-full px-3 py-1.5 pl-6 text-left text-xs hover:bg-blue-50 text-gray-800"
                    >
                      ↳ {it}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        );
      })()}
    </div>
  );
}
